/**
 * SiteHead — global head additions (Google verification, GA4 tag, sitewide
 * LocalBusiness JSON-LD). Mounted once in App.js.
 */
import { useEffect } from "react";
import { BRAND, PHONE_DISPLAY, INSTAGRAM_URL, TEL_HREF } from "@/lib/site";
import { installGlobalCtaTracking } from "@/lib/track";

const GOOGLE_VERIFICATION = process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || "";
const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";

function setMeta(name, content, attr = "name") {
    let el = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (!content) {
        if (el) el.remove();
        return;
    }
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function ensureScript(id, attrs, body) {
    let el = document.getElementById(id);
    if (el) return el;
    el = document.createElement("script");
    el.id = id;
    Object.entries(attrs || {}).forEach(([k, v]) => {
        if (v != null) el.setAttribute(k, v);
    });
    if (body) el.textContent = body;
    document.head.appendChild(el);
    return el;
}

function ensureSiteJsonLd() {
    let el = document.head.querySelector('script[data-jsonld="site"]');
    if (!el) {
        el = document.createElement("script");
        el.setAttribute("type", "application/ld+json");
        el.setAttribute("data-jsonld", "site");
        document.head.appendChild(el);
    }
    const data = {
        "@context": "https://schema.org",
        "@type": "AutoRepair",
        name: BRAND,
        url: typeof window !== "undefined" ? window.location.origin : "https://limitbreakperformance.com",
        telephone: PHONE_DISPLAY,
        priceRange: "$$",
        areaServed: [
            { "@type": "City", name: "Virginia Beach" },
            { "@type": "City", name: "Norfolk" },
            { "@type": "City", name: "Chesapeake" },
            { "@type": "City", name: "Portsmouth" },
            { "@type": "City", name: "Hampton" },
            { "@type": "City", name: "Newport News" },
        ],
        address: {
            "@type": "PostalAddress",
            addressLocality: "Virginia Beach",
            addressRegion: "VA",
            addressCountry: "US",
        },
        sameAs: [INSTAGRAM_URL].filter(Boolean),
        potentialAction: {
            "@type": "ReserveAction",
            target: TEL_HREF,
            name: "Call for a quote",
        },
    };
    el.textContent = JSON.stringify(data);
}

export default function SiteHead() {
    useEffect(() => {
        // Google Search Console domain verification (HTML meta variant)
        setMeta("google-site-verification", GOOGLE_VERIFICATION);

        // GA4 — only injects if a measurement ID is configured
        if (GA4_ID && /^G-[A-Z0-9]+$/.test(GA4_ID) && !document.getElementById("ga4-loader")) {
            ensureScript(
                "ga4-loader",
                { src: `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`, async: "" },
                null
            );
            ensureScript(
                "ga4-init",
                {},
                `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}gtag('js',new Date());gtag('config','${GA4_ID}',{anonymize_ip:true});`
            );
        }

        // Sitewide LocalBusiness JSON-LD (re-rendered when this mounts so location.origin is fresh)
        ensureSiteJsonLd();

        // Auto-track every tel:/sms: link click as a GA4 conversion event
        const uninstall = installGlobalCtaTracking();
        return uninstall;
    }, []);

    return null;
}
